// Triagly SDK Main Entry Point

import {
  TriaglyConfig,
  FeedbackData,
} from './types';
import { FeedbackWidget } from './ui';
import { TriaglyAPI } from './api';
import { collectMetadata, RateLimiter, ConsoleLogger } from './utils';

export class Triagly {
  private config: TriaglyConfig;
  private widget: FeedbackWidget;
  private api: TriaglyAPI;
  private rateLimiter: RateLimiter;
  private consoleLogger: ConsoleLogger | null = null;

  constructor(config: TriaglyConfig) {
    // Handle backward compatibility - support multiple key names
    let apiKey = config.apiKey || config.publishableKey;

    if (!apiKey && config.projectId) {
      console.warn(
        'Triagly: projectId is deprecated. Please use apiKey instead. ' +
        'See migration guide: https://docs.triagly.com/sdk/migration'
      );
      apiKey = config.projectId;
    }

    if (!apiKey) {
      throw new Error('Triagly: apiKey is required. Get yours at https://triagly.com/dashboard');
    }

    this.config = {
      theme: 'auto',
      position: 'bottom-right',
      buttonShape: 'rounded',
      buttonText: 'Feedback',
      placeholderText: 'Describe what happened...',
      successMessage: 'Feedback sent successfully!',
      errorMessage: 'Failed to send feedback. Please try again.',
      captureConsole: true,
      consoleLogLimit: 50,
      consoleLogLevels: ['error', 'warn'],
      ...config,
      apiKey,
      publishableKey: apiKey, // Keep for backward compatibility
    };

    this.api = new TriaglyAPI(
      apiKey,
      this.config.environment || 'production',
      this.config.apiUrl,
      this.config.getToken,
      this.config.turnstileSiteKey
    );

    // Always pass Turnstile site key to widget (from API which has default)
    this.config.turnstileSiteKey = this.api.getTurnstileSiteKey();
    this.widget = new FeedbackWidget(this.config);
    this.rateLimiter = new RateLimiter(apiKey, 3, 5 * 60 * 1000);

    // Initialize console logger if enabled
    if (this.config.captureConsole !== false) {
      this.consoleLogger = new ConsoleLogger(
        this.config.consoleLogLimit,
        this.config.consoleLogLevels
      );
      this.consoleLogger.start();
    }

    this.init();
  }

  /**
   * Initialize the SDK
   */
  private init(): void {
    // Wait for DOM to be ready before initializing widget
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.widget.init();
      });
    } else {
      // DOM is already ready
      this.widget.init();
    }

    // Listen for form submissions
    document.addEventListener('triagly:submit', (e: Event) => {
      const customEvent = e as CustomEvent;
      this.handleSubmit(customEvent.detail);
    });
  }

  /**
   * Handle feedback submission
   */
  private async handleSubmit(data: any): Promise<void> {
    try {
      // Check rate limit
      if (!this.rateLimiter.canProceed()) {
        const resetTime = Math.ceil(this.rateLimiter.getTimeUntilReset() / 1000 / 60);
        throw new Error(`Rate limit exceeded. Please try again in ${resetTime} minute(s).`);
      }

      // Collect metadata
      const metadata = collectMetadata(this.config.metadata);

      // Prepare feedback data
      const feedbackData: FeedbackData = {
        title: data.title,
        description: data.description,
        reporterEmail: data.reporterEmail,
        consoleLogs: this.consoleLogger?.getLogs(),
      };

      // Submit to API with Turnstile token if provided
      const response = await this.api.submitFeedback(
        feedbackData,
        metadata,
        data.turnstileToken
      );

      // Record rate limit attempt
      this.rateLimiter.recordAttempt();

      // Dispatch success event for UI layer
      document.dispatchEvent(new CustomEvent('triagly:success', {
        detail: { feedbackId: response.id }
      }));

      // Call success callback
      if (this.config.onSuccess) {
        this.config.onSuccess(response.id);
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error instanceof Error ? error.message : 'Unknown error');

      // Dispatch error event for UI layer
      document.dispatchEvent(new CustomEvent('triagly:error', {
        detail: error
      }));

      // Call error callback
      if (this.config.onError && error instanceof Error) {
        this.config.onError(error);
      }

      throw error;
    }
  }

  /**
   * Programmatically open the feedback widget
   */
  open(): void {
    this.widget.open();
  }

  /**
   * Programmatically close the feedback widget
   */
  close(): void {
    this.widget.close();
  }

  /**
   * Submit feedback programmatically without UI
   * Note: When Turnstile is enabled, you must use the widget UI or provide a token
   */
  async submit(data: FeedbackData, turnstileToken?: string): Promise<void> {
    // Require Turnstile token if configured
    if (this.config.turnstileSiteKey && !turnstileToken) {
      throw new Error(
        'Turnstile verification required. When Turnstile is enabled, you must:\n' +
        '1. Use the widget UI (triagly.open()), or\n' +
        '2. Implement Turnstile in your form and pass the token: triagly.submit(data, token)'
      );
    }

    const metadata = collectMetadata(this.config.metadata);

    // Include console logs if available and not already provided
    if (!data.consoleLogs && this.consoleLogger) {
      data.consoleLogs = this.consoleLogger.getLogs();
    }

    await this.api.submitFeedback(data, metadata, turnstileToken);
    this.rateLimiter.recordAttempt();
  }

  /**
   * Destroy the SDK instance
   */
  destroy(): void {
    this.widget.destroy();
    this.consoleLogger?.stop();
    document.removeEventListener('triagly:submit', () => {});
  }
}

// Export types
export * from './types';

// Auto-initialize if config is in window
if (typeof window !== 'undefined') {
  const globalConfig = (window as any).TriaglyConfig;
  if (globalConfig) {
    (window as any).triagly = new Triagly(globalConfig);
  }

  // Make Triagly class directly accessible as window.Triagly for UMD builds
  // This allows users to use: new Triagly({...}) instead of new Triagly.default({...})
  (window as any).Triagly = Triagly;
}

// Default export
export default Triagly;
